import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

interface Asset {
  id: number;
  filename: string;
  url: string;
  contentType: string;
  width?: number;
  height?: number;
  altText?: string;
  tags?: string;
  source: string;
  fileSizeBytes: number;
  createdAt: string;
}

export default function AssetsIndex() {
  const api = useApi();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [selected, setSelected] = useState<Asset | null>(null);
  const [editing, setEditing] = useState<{ altText: string; tags: string } | null>(null);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [copied, setCopied] = useState<number | null>(null);
  const [captureDesc, setCaptureDesc] = useState("");
  const [captureUrl, setCaptureUrl] = useState("");
  const [showCapture, setShowCapture] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ pageSize: "100" });
      if (filterSource) params.set("source", filterSource);
      const data = await (await api.admin.get(`api/kb/assets?${params}`)).json();
      setAssets(data.items || []);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [filterSource]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    try {
      setUploading(true);
      setError(null);
      const form = new FormData();
      form.append("file", file);
      await (await api.admin.post("api/kb/assets/upload", { body: form })).json();
      await load();
    } catch (err) {
      setError(`Upload failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const capture = async () => {
    if (!captureDesc.trim()) return;
    try {
      setCapturing(true);
      setError(null);
      await (await api.admin.post("api/kb/assets/capture", {
        body: JSON.stringify({ description: captureDesc, url: captureUrl || undefined }),
        headers: { "Content-Type": "application/json" }
      })).json();
      await load();
      setShowCapture(false);
      setCaptureDesc("");
      setCaptureUrl("");
    } catch (err) {
      setError(`Capture failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    } finally {
      setCapturing(false);
    }
  };

  const saveEdit = async () => {
    if (!selected || !editing) return;
    try {
      await api.admin.put(`api/kb/assets/${selected.id}`, {
        body: JSON.stringify(editing),
        headers: { "Content-Type": "application/json" }
      });
      setAssets(prev => prev.map(a => a.id === selected.id ? { ...a, ...editing } : a));
      setSelected(prev => prev ? { ...prev, ...editing } : null);
      setEditing(null);
    } catch (err) {
      setError(`Save failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    }
  };

  const deleteAsset = async (id: number) => {
    if (!confirm("Delete this asset? Any articles using it will show a broken image.")) return;
    try {
      await api.admin.delete(`api/kb/assets/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      setError(`Delete failed: ${err instanceof Response ? (err as Response).status : String(err)}`);
    }
  };

  const copyUrl = (asset: Asset) => {
    navigator.clipboard.writeText(asset.url);
    setCopied(asset.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const filtered = assets.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.filename?.toLowerCase().includes(q) ||
      a.altText?.toLowerCase().includes(q) ||
      a.tags?.toLowerCase().includes(q);
  });

  const fmtSize = (b: number) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div style={{ display: "flex", height: "calc(100vh - 120px)", gap: 0 }}>
      {/* Left: grid */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: "#fff" }}>
          <input
            placeholder="Search by name, alt text, or tag…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180, border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 12px", fontSize: 14 }}
          />
          <select
            value={filterSource}
            onChange={e => setFilterSource(e.target.value)}
            style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 10px", fontSize: 14, background: "#fff" }}
          >
            <option value="">All sources</option>
            <option value="upload">Uploaded</option>
            <option value="studio">Studio capture</option>
            <option value="polished">Polished</option>
          </select>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 14, fontWeight: 500, cursor: uploading ? "not-allowed" : "pointer" }}
          >
            {uploading ? "Uploading…" : "↑ Upload Image"}
          </button>
          <button
            onClick={() => setShowCapture(s => !s)}
            style={{ background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
          >
            📸 Capture from Studio
          </button>
        </div>

        {/* Studio capture panel */}
        {showCapture && (
          <div style={{ padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>
              Describe the screen you want. Studio will log into staging, navigate to it, and return a polished screenshot automatically.
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                placeholder="e.g. 'The SLA Policy config page with First Response Time set to 2 minutes'"
                value={captureDesc}
                onChange={e => setCaptureDesc(e.target.value)}
                style={{ flex: 2, minWidth: 260, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}
              />
              <input
                placeholder="Staging URL (optional override)"
                value={captureUrl}
                onChange={e => setCaptureUrl(e.target.value)}
                style={{ flex: 1, minWidth: 200, border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 14 }}
              />
              <button
                onClick={capture}
                disabled={capturing || !captureDesc.trim()}
                style={{
                  background: capturing ? "#94a3b8" : "#10b981", color: "#fff", border: "none",
                  borderRadius: 8, padding: "8px 20px", fontSize: 14, fontWeight: 500,
                  cursor: capturing ? "not-allowed" : "pointer"
                }}
              >
                {capturing ? "Capturing… (up to 60s)" : "Capture"}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 20px", background: "#fef2f2", color: "#dc2626", fontSize: 13, borderBottom: "1px solid #fecaca" }}>
            {error}
          </div>
        )}

        {/* Grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20, background: "#f9fafb" }}>
          {loading ? (
            <LoadingSpinner />
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: 60, fontSize: 15 }}>
              {search ? "No assets match your search." : "No assets yet. Upload an image or capture from Studio."}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16 }}>
              {filtered.map(asset => (
                <div
                  key={asset.id}
                  onClick={() => { setSelected(asset); setEditing(null); }}
                  style={{
                    border: `2px solid ${selected?.id === asset.id ? "#6366f1" : "#e5e7eb"}`,
                    borderRadius: 10, background: "#fff", cursor: "pointer", overflow: "hidden",
                    transition: "border-color 0.15s, box-shadow 0.15s",
                    boxShadow: selected?.id === asset.id ? "0 0 0 3px #e0e7ff" : "0 1px 4px rgba(0,0,0,0.06)"
                  }}
                >
                  <div style={{ height: 140, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
                    <img src={asset.url} alt={asset.altText || asset.filename}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                    <span style={{
                      position: "absolute", top: 6, right: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                      background: asset.source === "studio" ? "#ede9fe" : "#f0fdf4",
                      color: asset.source === "studio" ? "#7c3aed" : "#166534",
                      borderRadius: 4, padding: "2px 6px"
                    }}>
                      {asset.source.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#111827", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                      {asset.altText || asset.filename}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                      {asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ""}{fmtSize(asset.fileSizeBytes)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: detail panel */}
      {selected && (
        <div style={{ width: 320, borderLeft: "1px solid #e5e7eb", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>Asset Details</span>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#9ca3af" }}>×</button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
            {/* Preview */}
            <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 160 }}>
              <img src={selected.url} alt={selected.altText || selected.filename}
                style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 4 }} />
            </div>

            {/* Meta */}
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 16 }}>
              <div style={{ marginBottom: 6 }}><strong>File:</strong> {selected.filename}</div>
              {selected.width && <div style={{ marginBottom: 6 }}><strong>Size:</strong> {selected.width}×{selected.height}px</div>}
              <div style={{ marginBottom: 6 }}><strong>Weight:</strong> {fmtSize(selected.fileSizeBytes)}</div>
              <div style={{ marginBottom: 6 }}><strong>Source:</strong> {selected.source}</div>
              <div><strong>Created:</strong> {new Date(selected.createdAt).toLocaleDateString()}</div>
            </div>

            {/* Editable fields */}
            {editing ? (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Alt Text</label>
                <input
                  value={editing.altText}
                  onChange={e => setEditing(prev => prev ? { ...prev, altText: e.target.value } : null)}
                  placeholder="Describe the image for accessibility and SEO"
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}
                />
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Tags</label>
                <input
                  value={editing.tags}
                  onChange={e => setEditing(prev => prev ? { ...prev, tags: e.target.value } : null)}
                  placeholder="bot, workflow, routing (comma-separated)"
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, marginBottom: 14, boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveEdit} style={{ flex: 1, background: "#6366f1", color: "#fff", border: "none", borderRadius: 7, padding: "8px 0", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Save</button>
                  <button onClick={() => setEditing(null)} style={{ flex: 1, background: "#f1f5f9", color: "#374151", border: "none", borderRadius: 7, padding: "8px 0", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                {selected.altText && <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}><em>{selected.altText}</em></p>}
                {selected.tags && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                    {selected.tags.split(",").map(t => t.trim()).filter(Boolean).map(t => (
                      <span key={t} style={{ background: "#ede9fe", color: "#5b21b6", borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 500 }}>{t}</span>
                    ))}
                  </div>
                )}
                <button onClick={() => setEditing({ altText: selected.altText || "", tags: selected.tags || "" })}
                  style={{ width: "100%", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 7, padding: "7px 0", fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
                  Edit Alt Text &amp; Tags
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={() => copyUrl(selected)}
              style={{ background: copied === selected.id ? "#10b981" : "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
            >
              {copied === selected.id ? "✓ Copied!" : "Copy URL"}
            </button>
            <a
              href={selected.url}
              download={selected.filename}
              style={{ textAlign: "center", background: "#f1f5f9", color: "#374151", border: "none", borderRadius: 8, padding: "10px 0", fontSize: 14, cursor: "pointer", textDecoration: "none", display: "block" }}
            >
              Download
            </a>
            <button
              onClick={() => deleteAsset(selected.id)}
              style={{ background: "none", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}
            >
              Delete Asset
            </button>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
    </div>
  );
}
